import { useState, useEffect, useCallback, useRef } from 'react';
import { notification } from 'antd';
import axios from 'axios';

import { IFeedbackIA, IFeedbackPaginadoResponse } from './interface';

import FeedbackIAService from '../../services/http/services/InteligenciaArtificial/FeedbackIAService';

export const useFeedbacksIA = () => {
    const refContagemDoFiltro = useRef<Array<any>>([]);
    const signal = axios.CancelToken.source();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string>>([]);
    const [data, setData] = useState<IFeedbackIA[]>([]);
    const [hasSelectedView, setHasSelectedView] = useState<boolean>(false);
    const [loadingTable, setLoadingTable] = useState<boolean>(false);
    const [savingStatus, setSavingStatus] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedFeedback, setSelectedFeedback] = useState<IFeedbackIA | null>(null);

    // Form modal state
    const [formStatus, setFormStatus] = useState<string>('resolvido');
    const [formObservacao, setFormObservacao] = useState<string>('');

    const loadData = useCallback(() => {
        setLoadingTable(true);

        const loadFeedbacks = async () => {
            try {
                const response = await FeedbackIAService.getFeedbacks(undefined, signal.token);

                let feedbackList: IFeedbackIA[] = [];

                if (Array.isArray(response)) {
                    feedbackList = response;
                } else if (response && typeof response === 'object') {
                    const res = response as IFeedbackPaginadoResponse;
                    feedbackList = res.data || res.feedbacks || res.items || [];
                }

                setData(feedbackList);
                setLoadingTable(false);
            } catch (error: any) {
                if (error?.message !== 'Requisicao cancelada!') {
                    setLoadingTable(false);
                    notification.error({
                        message: 'Erro',
                        description: 'Não foi possível buscar os dados!',
                    });
                }
            }
        };

        loadFeedbacks();
    }, []);

    useEffect(() => {
        loadData();

        return () => {
            signal.cancel('Requisicao cancelada!');
        };
    }, []);

    const openModal = useCallback((feedback: IFeedbackIA) => {
        setSelectedFeedback(feedback);
        setFormStatus(feedback.status || 'resolvido');
        setFormObservacao(feedback.observacao_interna || '');
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedFeedback(null);
        setFormObservacao('');
    }, []);

    const clickBotaoVisualizar = useCallback(() => {
        if (selectedRowKeys.length === 1) {
            const selected = data.find(
                (item) => String(item._id || item.id || item.feedback_id) === selectedRowKeys[0]
            );
            if (selected) {
                openModal(selected);
            }
        }
    }, [selectedRowKeys, data, openModal]);

    const onclickTable = useCallback((record: IFeedbackIA, row: any): void => {
        const key = String(record._id || record.id || record.feedback_id);
        if (selectedRowKeys.includes(key)) {
            setSelectedRowKeys([]);
            setHasSelectedView(false);
        } else {
            setSelectedRowKeys([key]);
            setHasSelectedView(true);
        }
    }, [selectedRowKeys]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasSelectedView(false);
    }, []);

    const onSelectChange = useCallback((newSelectedRowKeys: any[]): void => {
        if (newSelectedRowKeys.length > 1) {
            setSelectedRowKeys([String(newSelectedRowKeys[1])]);
            setHasSelectedView(true);
        } else if (newSelectedRowKeys.length === 0) {
            setSelectedRowKeys([]);
            setHasSelectedView(false);
        } else {
            setSelectedRowKeys([String(newSelectedRowKeys[0])]);
            setHasSelectedView(true);
        }
    }, []);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: 50,
        type: 'radio' as any,
    };

    const handleSaveStatus = useCallback(async () => {
        if (!selectedFeedback) return;

        const feedbackId = selectedFeedback._id || selectedFeedback.id || selectedFeedback.feedback_id;
        if (!feedbackId) {
            notification.warning({
                message: 'Aviso',
                description: 'Identificador do feedback não encontrado.',
            });
            return;
        }

        try {
            setSavingStatus(true);
            await FeedbackIAService.updateStatusFeedback(
                feedbackId,
                {
                    status: formStatus,
                    observacao_interna: formObservacao.trim() || undefined,
                }
            );

            notification.success({
                message: 'Sucesso',
                description: 'Feedback atualizado com sucesso!',
            });

            closeModal();
            handleResetRowSelection();
            loadData();
        } catch (error: any) {
            notification.error({
                message: 'Erro ao atualizar feedback',
                description:
                    error?.response?.data?.message ||
                    'Não foi possível atualizar o status do feedback.',
            });
        } finally {
            setSavingStatus(false);
        }
    }, [selectedFeedback, formStatus, formObservacao, closeModal, handleResetRowSelection, loadData]);

    return {
        data,
        loadingTable,
        savingStatus,
        isModalOpen,
        selectedFeedback,
        formStatus,
        formObservacao,
        refContagemDoFiltro,
        hasSelectedView,
        rowSelection,
        onclickTable,
        clickBotaoVisualizar,
        handleResetRowSelection,
        setFormStatus,
        setFormObservacao,
        openModal,
        closeModal,
        handleSaveStatus,
        loadData,
    };
};
